import React, { useState } from 'react';
import { TextField, InputAdornment, IconButton, CircularProgress } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import { useTranslate } from 'react-admin';
import { configApi } from '../services/configApi';

export type SearchField = 'address' | 'ceel_number' | 'sscc' | 'old_address';

interface PlaceSearchProps {
  terminalId: number;
  onSearch: (placeIds: number[]) => void;
  searchField?: SearchField;
  searchFields?: SearchField[];
  placeholder: string;
  icon?: React.ReactNode;
}

export const PlaceSearch: React.FC<PlaceSearchProps> = ({
  terminalId,
  onSearch,
  searchField,
  searchFields,
  placeholder,
  icon
}) => {
  const translate = useTranslate();
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!searchValue.trim()) return;
    setLoading(true);
    setError(null);

    const fieldsToSearch: SearchField[] = searchFields?.length
      ? searchFields
      : searchField
        ? [searchField]
        : ['address'];

    try {
      const resultIds = await configApi.searchStoragePlaces(
        terminalId,
        searchValue.trim(),
        fieldsToSearch
      );

      if (resultIds && resultIds.length > 0) {
        onSearch(resultIds);
        setError(null);
      } else {
        onSearch([] as number[]);
        setError(translate('osfc.storage_ui.search.no_places_found'));
      }
    } catch (err) {
      console.error(err);
      setError(translate('osfc.storage_ui.search.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setSearchValue('');
    setError(null);
    onSearch([] as number[]);
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder={placeholder}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyPress={handleKeyPress}
        error={!!error}
        helperText={error}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {searchValue && (
                <IconButton onClick={handleClear} disabled={loading} size="small">
                  <ClearIcon />
                </IconButton>
              )}
              <IconButton onClick={handleSearch} disabled={loading} size="small">
                {loading ? <CircularProgress size={20} /> : (icon || <SearchIcon />)}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
};
